import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, Button, Avatar, Spin, Alert } from "antd";
import { UserOutlined } from "@ant-design/icons";
import "./../style/header.css"

const Dashboard = () => {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");

  const localBusinessId =
    typeof window !== "undefined" ? localStorage.getItem("localbusinessId") : null;

  // nicely formatted single-line address
  const addressLine = useMemo(() => {
    if (!business?.address) return "";
    const a = business.address;
    // Prefer city as string from API; others may be numeric IDs
    const parts = [
      a.street_address_1,
      a.street_address_2,
      a.landmark,
      a.city?.name,
      a.state?.name,
      a.pincode,
    ].filter(Boolean);
    return parts.join(", ");
  }, [business]);

  useEffect(() => {
    const fetchBusiness = async () => {
      if (!localBusinessId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setApiError("");
      try {
        const res = await fetch(`https://biz.dropyourbusiness.com/api/business/${localBusinessId}`);
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          setApiError(json?.message || "Failed to fetch business.");
          setLoading(false);
          return;
        }

        const biz = json?.business;
        if (!biz) {
          setApiError("Business not found.");
          setLoading(false);
          return;
        }

        // normalize business_time
        const normalized = {
          ...biz,
          business_time: {
            ...biz.business_time,
            is_24_7: Number(biz?.business_time?.is_24_7) === 1,
            working_hours:
              typeof biz?.business_time?.working_hours === "string"
                ? (() => {
                    try {
                      return JSON.parse(biz.business_time.working_hours);
                    } catch {
                      return {};
                    }
                  })()
                : biz?.business_time?.working_hours || {},
          },
        };

        setBusiness(normalized);
        // keep in localStorage for header reuse
        localStorage.setItem("business", JSON.stringify(normalized));
      } catch (err) {
        console.error(err);
        setApiError("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchBusiness();
  }, [localBusinessId]);

  // If no business id saved, nudge user to add one
  if (!localBusinessId) {
    return (
      <div style={{ maxWidth: 1000, margin: "24px auto", padding: "0 16px" }}>
        <h1 style={{ marginBottom: 16 }}>Welcome</h1>
        <Alert
          type="info"
          showIcon
          message="No business found"
          description="You haven't added a business yet. Click the button below to create your profile."
          style={{ marginBottom: 16 }}
        />
        <Button type="primary" onClick={() => navigate("/add-listing")}>
          Add a business
        </Button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1100, margin: "24px auto", padding: "0 16px" }} className="dashboard-main">
      <Spin spinning={loading} tip="Loading...">
        {!loading && (
          <>
            <h1 style={{ fontWeight: 600, marginBottom: 16 }}>
              Welcome{business?.business_name ? `, ${business.business_name}` : ""}
            </h1>

            {apiError ? (
              <Alert
                type="error"
                showIcon
                message="Error"
                description={apiError}
                style={{ marginBottom: 16 }}
              />
            ) : null}

            <Row gutter={[24, 24]}>
              {/* Left column: View your profiles */}
              <Col xs={24} md={16}>
                <h2 style={{ marginBottom: 8 }}>View your profiles</h2>
                <p style={{ color: "#666", marginTop: 0 }}>
                  Here are the profiles you have added
                </p>

                {/* Profile card list (single) */}
                <Card
                  style={{ marginBottom: 16 }}
                  bodyStyle={{ padding: 16 }}
                >
                  <Row align="middle" justify="space-between" gutter={[16, 16]}>
                    <Col flex="auto">
                      <Row gutter={12} wrap={false} align="middle">
                        <Col>
                          {business?.logo ? (
                            <Avatar
                              shape="square"
                              size={48}
                              src={business.logo}
                              alt={business.business_name}
                              style={{ objectFit: "cover" }}
                            />
                          ) : (
                            <Avatar shape="square" size={48} icon={<UserOutlined />} />
                          )}
                        </Col>
                        <Col flex="auto">
                          <div style={{ fontWeight: 600 }}>
                            {business?.business_name || "Business"}
                          </div>
                          <div style={{ color: "#666" }}>
                            {addressLine || "—"}
                          </div>
                        </Col>
                      </Row>
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        onClick={() => navigate("/business-listing")}
                      >
                        View profile
                      </Button>
                    </Col>
                  </Row>
                </Card>

                {/* <Button onClick={() => navigate("/add-listing")}>
                  Add a business
                </Button> */}
              </Col>

              {/* Right column: Claim CTA */}
              <Col xs={24} md={8}>
                <h2 style={{ marginBottom: 8 }}>Got a profile you want to claim?</h2>
                <p style={{ color: "#666", marginTop: 0 }}>
                  Please search for the profile and click the &apos;claim profile&apos; button
                </p>
                <Button type="primary" onClick={() => navigate("/listings")}>
                  Search Now
                </Button>
              </Col>
            </Row>

            {/* Optional: details section */}
            {/* <div style={{ marginTop: 32 }}>
              <Card title="Business details" bodyStyle={{ paddingTop: 12 }}>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <strong>Owner:</strong> {business?.owner_name || "—"}
                  </Col>
                  <Col xs={24} md={12}>
                    <strong>Status:</strong> {business?.status || "—"}
                  </Col>
                  <Col xs={24} md={12}>
                    <strong>Email:</strong> {business?.address?.email || "—"}
                  </Col>
                  <Col xs={24} md={12}>
                    <strong>Phone:</strong> {business?.address?.contact_1 || "—"}
                  </Col>
                  <Col xs={24} md={12}>
                    <strong>Category:</strong> {business?.category || "—"}
                  </Col>
                  <Col xs={24} md={12}>
                    <strong>Subcategory:</strong> {business?.sub_category || "—"}
                  </Col>
                </Row>

                {Array.isArray(business?.images) && business.images.length > 0 && (
                  <>
                    <div style={{ marginTop: 16, fontWeight: 600 }}>Images</div>
                    <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
                      {business.images.map((img, idx) => (
                        <Col key={idx}>
                          <img
                            src={img}
                            alt={`img-${idx}`}
                            style={{
                              width: 100,
                              height: 100,
                              objectFit: "cover",
                              borderRadius: 8,
                              border: "1px solid #eee",
                            }}
                          />
                        </Col>
                      ))}
                    </Row>
                  </>
                )}

              
                <div style={{ marginTop: 16, fontWeight: 600 }}>Business hours</div>
                {business?.business_time?.is_24_7 ? (
                  <div style={{ marginTop: 8 }}>Open 24/7</div>
                ) : (
                  <div style={{ marginTop: 8 }}>
                    {business?.business_time?.working_hours
                      ? Object.entries(business.business_time.working_hours).map(
                          ([day, t]) => (
                            <div key={day}>
                              <strong>{day}:</strong>{" "}
                              {(t?.open && t?.close) ? `${t.open} – ${t.close}` : "—"}
                            </div>
                          )
                        )
                      : "—"}
                  </div>
                )}
              </Card>
            </div> */}
          </>
        )}
      </Spin>
    </div>
  );
};

export default Dashboard;
